import { saveViewerToken, getViewerToken } from './utils/viewerAccess';
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import JoinScreen from './screens/JoinScreen';
import LandingScreen from './screens/LandingScreen';
import WaitingScreen from './screens/WaitingScreen';
import ViewerScreen from './screens/ViewerScreen';
import EndedScreen from './screens/EndedScreen';
import GalleryScreen from './screens/GalleryScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import TermsScreen from './screens/TermsScreen';
import SupportScreen from './screens/SupportScreen';
import './App.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://peekr-production.up.railway.app';

// A buyer who opens /join/CODE (shared link or QR) must never have to type the
// code. Read it synchronously from the URL so JoinScreen mounts straight on the
// lead step with the code already in hand — NOT after a post-mount effect (which
// left buyers stuck on the manual code-entry screen).
function deepLinkCodeFromUrl() {
  const m = window.location.pathname.match(/\/join\/([A-Z0-9-]+)/i);
  return m ? m[1].toUpperCase() : '';
}

export default function App() {
  // Root shows the agent landing page; buyers with codes flow into 'join'.
  const [screen, setScreen] = useState(() => {
    const p = window.location.pathname;
    return (p === '/' || p === '') ? 'landing' : 'join';
  });
  const [roomCode, setRoomCode] = useState(deepLinkCodeFromUrl);
  const [viewerCount, setViewerCount] = useState(0);
  const [content, setContent] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [joinError, setJoinError] = useState('');
  const [isLiveActive, setIsLiveActive] = useState(false);
  const [liveMode, setLiveMode] = useState('mjpeg'); // 'mjpeg' (legacy) | 'livekit' (WebRTC), set by live_start
  const [measurements, setMeasurements] = useState([]); // Realtor Mode overlay array
  const [videoUploadPct, setVideoUploadPct] = useState(null); // host video upload % (relayed)
  const [contentPreview, setContentPreview] = useState(null); // instant low-res preview (data URI) before the full image
  const [highlightToast, setHighlightToast] = useState(null); // host curated N highlights → transient banner
  const [pdfInfo, setPdfInfo] = useState(null); // { pdfUrl, pdfTitle } when the deck came from a PDF
  // Event mode state
  const [eventMode, setEventMode] = useState(false);
  const [eventName, setEventName] = useState('');
  const [guestUploadEnabled, setGuestUploadEnabled] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryInfo, setGalleryInfo] = useState(null);
  const [agentBrand, setAgentBrand] = useState(null); // realtor white-label (null = plain Peekr)
  const [celebration, setCelebration] = useState(false); // event without listing specs → wedding/party framing
  const [deepLinkCode, setDeepLinkCode] = useState(deepLinkCodeFromUrl); // /join/CODE → prefill lead gate at mount
  const socketRef = useRef(null);
  const liveImgRef = useRef(null);

  useEffect(() => {
    // Always tear down any live socket when App unmounts, regardless of which
    // route path was taken below. Prevents leaked connections + listeners.
    const cleanup = () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };

    const path = window.location.pathname;
    if (path === '/privacy' || path === '/terms' || path === '/support') return cleanup;

    // Gallery view route: /gallery/ROOMCODE
    const galleryMatch = path.match(/\/gallery\/([A-Z0-9-]+)/i);
    if (galleryMatch) {
      const code = galleryMatch[1].toUpperCase();
      setRoomCode(code);
      loadGallery(code);
      return cleanup;
    }

    // Join room route: /join/ROOMCODE — route through the lead-capture gate with
    // the code prefilled (buyer goes straight to the details step, then joins).
    const joinMatch = path.match(/\/join\/([A-Z0-9-]+)/i);
    if (joinMatch) {
      const code = joinMatch[1].toUpperCase();
      setRoomCode(code);
      setDeepLinkCode(code);
      setScreen('join');
    }
    return cleanup;
  }, []);

  // Auto-dismiss the highlight toast a few seconds after it appears.
  useEffect(() => {
    if (highlightToast == null) return;
    const t = setTimeout(() => setHighlightToast(null), 6000);
    return () => clearTimeout(t);
  }, [highlightToast]);

  const loadGallery = async (code) => {
    try {
      const res = await fetch(`${SERVER_URL}/gallery/${code}`);
      if (!res.ok) {
        setJoinError('Listing photos not found or expired.');
        setScreen('join');
        return;
      }
      const data = await res.json();
      setGalleryInfo(data);
      setGallery(data.photos || []);
      setEventName(data.eventName || code);
      setAgentBrand(data.brand || null);
      setCelebration(!!data.celebration);
      setScreen('gallery');
    } catch (err) {
      setJoinError('Could not load the listing photos.');
      setScreen('join');
    }
  };

  const joinRoom = (code) => {
    // Tear down any previous socket first so a retry/re-join can't leave an
    // orphaned connection with its own duplicate listeners firing setState.
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = io(SERVER_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1500,
    });
    socketRef.current = socket;
    // Fresh socket per call + disconnect-before-create above guarantees each
    // listener below is registered exactly once (no double-registration).
    socket.removeAllListeners();

    let joinAttempts = 0;
    const MAX_JOIN_ATTEMPTS = 8;
    const JOIN_RETRY_MS = 1500;
    let joinTimer = null;

    const attemptJoin = () => {
      joinAttempts++;
      socket.emit('join_room', { roomCode: code, token: getViewerToken(code) });
    };

    socket.on('connect', () => {
      attemptJoin();
    });

    socket.on('reconnect', () => {
      attemptJoin();
    });

    socket.on('joined_room', ({ viewerCount, eventMode: em, eventName: en, guestUploadEnabled: gue, galleryCount, brand, celebration: cel }) => {
      clearTimeout(joinTimer);
      setViewerCount(viewerCount);
      setEventMode(em || false);
      setEventName(en || '');
      setGuestUploadEnabled(gue || false);
      setAgentBrand(brand || null);
      setCelebration(!!cel);
      // Move to waiting from join or connecting state
      setScreen(prev => (prev === 'join' || prev === 'connecting') ? 'waiting' : prev);
    });

    socket.on('viewer_count', ({ count }) => setViewerCount(count));

    // Host video upload progress — show viewers activity instead of nothing.
    socket.on('upload-progress', ({ percent }) => {
      setVideoUploadPct(percent >= 100 ? null : percent);
    });

    // Instant low-res preview — show it immediately; the full image swaps in
    // when content_update (the hosted URL) arrives.
    socket.on('content-preview', ({ preview }) => {
      setContentPreview(preview);
      setIsLiveActive(false);
      setSlides([]);
      setScreen(prev => (prev === 'join' || prev === 'connecting' || prev === 'waiting') ? 'viewing' : prev);
    });

    socket.on('live_frame', ({ frame }) => {
      if (liveImgRef.current) {
        liveImgRef.current.src = `data:image/jpeg;base64,${frame}`;
      }
    });

    // Realtor Mode — multi-measurement overlay events
    socket.on('measurement-add',       ({ measurement }) => setMeasurements(prev => [...prev, measurement]));
    socket.on('measurement-remove',    ({ id })          => setMeasurements(prev => prev.filter(m => m.id !== id)));
    socket.on('measurement-clear-all', ()                => setMeasurements([]));
    socket.on('measurement-add-batch', ({ measurements: batch }) => setMeasurements(batch));

    socket.on('content_update', (data) => {
      if (data.type === 'live_start') {
        setIsLiveActive(true);
        setLiveMode(data.mode === 'livekit' ? 'livekit' : 'mjpeg');
        setSlides([]);
        setContent(null);
        setContentPreview(null);
        setScreen('viewing');
        return;
      }
      if (data.type === 'live_end') {
        setIsLiveActive(false);
        return;
      }
      setIsLiveActive(false);
      setSlides([]);
      setContent(data);
      if (data.type !== 'image') setContentPreview(null); // keep preview as the image placeholder; clear for video
      setScreen('viewing');
    });

    socket.on('presentation_update', ({ slides, currentSlide, pdfUrl, pdfTitle }) => {
      setSlides(slides);
      setCurrentSlide(currentSlide);
      setPdfInfo(pdfUrl ? { pdfUrl, pdfTitle } : null);
      setContent(null);
      setScreen('viewing');
    });

    socket.on('slide_changed', ({ currentSlide }) => {
      setCurrentSlide(currentSlide);
    });

    // Gallery updates from host or other guests
    socket.on('gallery_update', ({ gallery: g }) => {
      setGallery(g);
    });

    // Live caption pop-in — Haiku finished captioning a shared/uploaded photo.
    // Patch it onto the matching gallery entry so the caption appears without a
    // refetch. No-op if that photo isn't in the loaded set yet.
    socket.on('photo_caption_ready', ({ photoIndex, caption, photoUrl }) => {
      setGallery(prev => prev.map((p, i) =>
        (i === photoIndex || p.url === photoUrl) ? { ...p, caption } : p
      ));
    });

    // Host curated a highlight reel (Pro auto-highlight) — show viewers a
    // transient toast linking to the gallery.
    socket.on('highlights_ready', ({ count }) => {
      if (count > 0) setHighlightToast(count);
    });

    // Guest upload toggle changed by host
    socket.on('guest_uploads_toggled', ({ enabled }) => {
      setGuestUploadEnabled(enabled);
    });

    socket.on('session_ended', ({ galleryAvailable, galleryCode, photoCount }) => {
      if (galleryAvailable && photoCount > 0) {
        // Redirect to gallery view
        setGalleryInfo({ photoCount, isLive: false });
        setScreen('ended');
      } else {
        setScreen('ended');
      }
      socket.disconnect();
    });

    socket.on('error', ({ message }) => {
      // If room not found, retry — host may not have connected yet
      if (/Room not found|Showing not found/i.test(message) && joinAttempts < MAX_JOIN_ATTEMPTS) {
        clearTimeout(joinTimer);
        joinTimer = setTimeout(() => {
          attemptJoin();
        }, JOIN_RETRY_MS);
        return;
      }
      // Final failure after all retries
      clearTimeout(joinTimer);
      setJoinError(message);
      setScreen('join');
      socket.disconnect();
    });
  };

  const handleJoin = async (code, lead, meta) => {
    const clean = code.trim().toUpperCase();
    setJoinError('');
    setRoomCode(clean);
    // JoinScreen pre-fetched room meta for gate theming — adopt it early so the
    // connecting/waiting screens speak the right language before joined_room
    // arrives (which then overwrites authoritatively).
    if (meta) {
      setCelebration(!!meta.celebration);
      if (meta.eventName) setEventName(meta.eventName);
    }
    setScreen('connecting');
    try {
      const response = await fetch(`${SERVER_URL}/lead/${clean}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(lead || {}),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not join showing.');
      saveViewerToken(clean, result.token);
      joinRoom(clean);
    } catch (error) { setJoinError(error.message); setScreen('join'); }
  };

  const handleGuestUpload = async (imageUrl, guestName) => {
    if (!socketRef.current || !guestUploadEnabled) return;
    socketRef.current.emit('guest_upload', { 
      roomCode, 
      imageUrl, 
      guestName: guestName || 'Guest' 
    });
  };

  // Viewer-facing video-upload banner (relayed from the host). Fixed overlay so
  // viewers see "Video uploading… X%" instead of nothing while a video uploads.
  const uploadBanner = videoUploadPct != null ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
      background: '#0B1628', borderBottom: '1px solid #122038', color: '#00D4FF',
      textAlign: 'center', padding: '10px 16px', fontSize: 14, fontWeight: 600,
    }}>
      📤 Video uploading… {videoUploadPct}%
    </div>
  ) : null;

  // Highlight-reel toast (relayed from the host's Pro auto-highlight run).
  const highlightBanner = highlightToast != null ? (
    <a href={`/gallery/${roomCode}`} style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2100, textDecoration: 'none',
      background: 'linear-gradient(135deg, #6E7BFF, #00E0FF)', color: '#070A12',
      textAlign: 'center', padding: '10px 16px', fontSize: 14, fontWeight: 700,
    }}>
      ✨ {highlightToast} highlight moment{highlightToast !== 1 ? 's' : ''} curated — tap to view the listing photos
    </a>
  ) : null;

  if (window.location.pathname === '/privacy') return <PrivacyScreen />;
  if (window.location.pathname === '/support') return <SupportScreen />;
  if (window.location.pathname === '/terms') return <TermsScreen />;
  
  if (screen === 'gallery') {
    return <GalleryScreen
      gallery={gallery}
      eventName={eventName}
      roomCode={roomCode}
      galleryInfo={galleryInfo}
      agentBrand={agentBrand}
    />;
  }
  
  if (screen === 'landing') return (
    <LandingScreen
      onJoinWithCode={(code) => { setRoomCode(code); setDeepLinkCode(code); setScreen('join'); }}
    />
  );
  if (screen === 'join') return <JoinScreen onJoin={handleJoin} error={joinError} initialCode={deepLinkCode} />;
  if (screen === 'connecting') return (
    <div className="screen">
      <div style={{ fontSize: 52, marginBottom: 16 }}>🔗</div>
      <div className="brand">Peekr</div>
      <div className="tagline">{celebration ? 'Connecting you live' : 'Connecting to showing'}<br />{roomCode}</div>
      <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'center' }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            width: 10, height: 10, borderRadius: '50%',
            background: '#00D4FF',
            animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
      <div className="sub" style={{ marginTop: 20 }}>No app needed · getpeekr.com</div>
    </div>
  );
  if (screen === 'waiting') return (<>{uploadBanner}{highlightBanner}<WaitingScreen roomCode={roomCode} viewerCount={viewerCount} eventMode={eventMode} eventName={eventName} celebration={celebration} /></>);
  if (screen === 'viewing') return (
    <>
    {uploadBanner}
    {highlightBanner}
    <ViewerScreen
      content={content}
      slides={slides}
      currentSlide={currentSlide}
      roomCode={roomCode}
      isLiveActive={isLiveActive}
      liveMode={liveMode}
      liveImgRef={liveImgRef}
      eventMode={eventMode}
      eventName={eventName}
      celebration={celebration}
      guestUploadEnabled={guestUploadEnabled}
      agentBrand={agentBrand}
      gallery={gallery}
      onGuestUpload={handleGuestUpload}
      measurements={measurements}
      contentPreview={contentPreview}
      pdfInfo={pdfInfo}
    />
    </>
  );
  if (screen === 'ended') return <EndedScreen roomCode={roomCode} galleryAvailable={galleryInfo?.photoCount > 0} celebration={celebration} />;
  return null;
}
